/**
 * The tracking-number text we send the family once a Canada Post label exists.
 *
 * Both delivery messages already promise this one — the invite closes with
 * "We'll send tracking once it's on its way", the confirmation with "we'll ship
 * to X and share the tracking number with you once they're on their way" — and
 * until now nothing ever sent it. The PIN was stored, polled and shown to staff
 * only. This is the other half of a promise we were already making.
 *
 * Kept pure and separate from the modal so the copy can be read and changed
 * without reading MUI, mirroring `buildPaperworkCompleteMessage`.
 */

export interface TrackingMessageParams {
  trackingPin: string;
  /** Who we're texting — the family contact, not necessarily the addressee. */
  recipientFirstName?: string | null;
  deceasedFirstName?: string | null;
  /** The urn travels from the supplier, not with the ashes. */
  urnTrackingNumber?: string | null;
}

export function buildTrackingMessage(
  language: 'en' | 'fr',
  { trackingPin, recipientFirstName, deceasedFirstName, urnTrackingNumber }: TrackingMessageParams
): string {
  // A deep link to this parcel, not the tracking homepage: a family that has
  // just been told their ashes are moving should not have to copy a 16-digit
  // number into a form. Language-matched, because Canada Post serves both.
  // Names arrive from hand-typed client rows ("Chris "): trim, or "Hi Chris ,".
  recipientFirstName = recipientFirstName?.trim() || null;
  deceasedFirstName = deceasedFirstName?.trim() || null;
  const link = `https://www.canadapost-postescanada.ca/track-reperage/${
    language === 'fr' ? 'fr' : 'en'
  }/details/${encodeURIComponent(trackingPin)}`;

  if (language === 'fr') {
    const lead = recipientFirstName ? `Bonjour ${recipientFirstName},` : 'Bonjour,';
    const whose = deceasedFirstName ? `de ${deceasedFirstName}` : 'de votre proche';
    const urn = urnTrackingNumber
      ? ` L'urne est expédiée séparément. Son numéro de suivi est ${urnTrackingNumber}.`
      : '';
    return (
      `${lead} les cendres ${whose} sont en route vers vous. Numéro de suivi Postes Canada : ${trackingPin}.` +
      ` Vous pouvez suivre la livraison ici : ${link}${urn}`
    );
  }

  const lead = recipientFirstName ? `Hi ${recipientFirstName},` : 'Hello,';
  const whose = deceasedFirstName ? `${deceasedFirstName}'s` : "your loved one's";
  const urn = urnTrackingNumber
    ? ` The urn ships separately. Its tracking number is ${urnTrackingNumber}.`
    : '';
  return (
    `${lead} ${whose} remains are on their way to you. Canada Post tracking number: ${trackingPin}.` +
    ` You can follow the delivery here: ${link}${urn}`
  );
}
